import { concat } from '@ember/helper';
import { eq, or } from 'tio-ui/utilities';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import DownloadButton from 'tio-reimagined/components/download-button';
import DefinitionList from 'tio-common/components/ui/definition-list';
import Modal from 'tio-reimagined/components/modal';
import Button from 'tio-reimagined/components/button';
import { Divider } from 'tio-ui/components/utilities';
import { VStack } from 'tio-ui/components/layout';
import { PhEye, PhLink, PhArrowRight, PhUserCheck } from 'ember-phosphor-icons';
import Chip from 'tio-reimagined/components/chip';
import Popover from 'tio-reimagined/components/popover';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type { PslfForm } from 'tio-common/models/search';
import type IntlService from 'ember-intl/services/intl';
import type StoreService from 'tio-employee/services/store';
import type SessionContextService from 'tio-employee/services/session-context';
import type { NotificationsService } from 'tio-reimagined/components/notifications';

interface PslfAdminCompanyFormsTableRowSig {
  Args: {
    model: PslfForm;
    rowClass: string;
    cellClass: string;
  };
  Element: HTMLTableRowElement;
}

export default class PslfAdminCompanyFormsTableRowComponent extends Component<PslfAdminCompanyFormsTableRowSig> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare sessionContext: SessionContextService;

  @tracked showDetailsModal = false;
  @tracked formDetails?: PslfFormModel;
  @tracked isLoadingDetails = false;

  get isPriorEmployerForm() {
    return this.args.model?.formType === 'priorEmployer';
  }

  get isNonPartnerApproverForm() {
    return !!this.args.model?.formNonPartner;
  }

  get formTypeDisplayKey() {
    if (this.isPriorEmployerForm && !this.isNonPartnerApproverForm) {
      return 'currentEmployerPriorPeriod';
    }
    return this.args.model.formType;
  }

  get needsReview() {
    return this.args.model?.formStatus === 'borrower_signed' && !this.isNonPartnerApproverForm;
  }

  get isOboForm() {
    return !!this.args.model?.pslfFormIsSubmittedOboBoolean;
  }

  get isOboByCurrentEmployee() {
    return (
      this.args.model?.pslfFormSubmittedOboByEmployeeIdKeyword ===
      this.sessionContext.currentEmployee?.id
    );
  }

  get approverDisplayEmail() {
    const model = this.args.model;

    if (model.formPrintAndMail) {
      return `${this.intl.t('pslf.table.not_applicable')} - ${this.intl.t(
        'pslf.table.print_and_mail'
      )}`;
    }
    if (model.employerClosed) {
      return `${this.intl.t('pslf.table.not_applicable')} - ${this.intl.t(
        'pslf.table.prior_employer_closed'
      )}`;
    }
    if (model.formNonPartner) {
      return model.borrowerEmployerEmail || '';
    }
    return model.employerEmail || '';
  }

  get normalizedEmployerSignDate() {
    const date = this.args.model.employerSignDate;
    if (!date) return '';
    // old forms include the timestamp
    if (date.includes('T')) {
      return date.split('T')[0]!;
    }
    return date;
  }

  get normalizedBorrowerSignDate() {
    const date = this.args.model.borrowerSignDate;
    if (!date) return '';
    // old forms include the timestamp
    if (date.includes('T')) {
      return date.split('T')[0]!;
    }
    return date;
  }

  needsUrl = async () => {
    let form = await this.store.findRecord('pslf-form', this.args.model.formId, {
      include: 'attachments',
    });

    // @ts-expect-error: this will be addressed with ed update
    const attachment = form.attachments?.find((attachment) => {
      return attachment.tags.includes(`document_id:${this.args.model.documentId}`);
    });

    return attachment?.signedUrl;
  };

  openDetailsModal = async () => {
    this.showDetailsModal = true;
    this.isLoadingDetails = true;
    try {
      this.formDetails = await this.store.findRecord('pslf-form', this.args.model.formId, {
        include: 'attachments',
      });
    } catch (e) {
      console.error('Failed to load form details', e);
    } finally {
      this.isLoadingDetails = false;
    }
  };

  closeDetailsModal = () => {
    this.showDetailsModal = false;
    this.formDetails = undefined;
  };

  get formFein() {
    return this.formDetails?.employerFields?.['employer-fein'] || '';
  }

  get employmentStartDate() {
    return this.formDetails?.employerFields?.['employment-start-date'] || '';
  }

  get employmentEndDate() {
    if (this.formDetails?.employerFields?.['employment-continues']) {
      return this.intl.t('pslf.table.present');
    }
    return this.formDetails?.employerFields?.['employment-end-date'] || '';
  }

  get employmentHours() {
    return this.formDetails?.employerFields?.['employment-hours'] || '';
  }

  copySignLink = async () => {
    try {
      await navigator.clipboard.writeText(this.formDetails?.employerSignLink || '');
      this.notifications.add(this.intl.t('pslf.notifications.copy_success'), {
        appearance: 'success',
      });
    } catch (e) {
      this.notifications.add(this.intl.t('pslf.notifications.copy_error'), {
        appearance: 'error',
      });
      console.error(e);
    }
  };

  <template>
    <tr class={{@rowClass}} ...attributes>
      <td class={{@cellClass}}>{{@model.payrollId}}</td>
      <td class={{@cellClass}}>{{@model.borrowerName}}</td>
      {{#if (eq @model.formStatus "borrower_signed")}}
        <td class="{{@cellClass}} text-red-600 items-center font-semibold truncate underline">
          {{#if this.needsReview}}
            <LinkTo
              data-test-form-review-link
              @route="authenticated.admin.pslf.form.review"
              @model={{@model.formId}}
            >
              {{t "common.pslf.status.needs_review"}}
            </LinkTo>
          {{/if}}
        </td>
      {{else}}
        <td class={{@cellClass}}>{{t (concat "common.pslf.status." @model.formStatus)}}</td>
      {{/if}}
      <td class={{@cellClass}}>{{this.approverDisplayEmail}}</td>
      {{#let this.normalizedBorrowerSignDate as |date|}}
        <td class={{@cellClass}} title={{date}}>
          {{#if this.isOboForm}}
            <div class="flex flex-col gap-1">
              <Popover @triggerMode="hover">
                <:trigger as |pop|>
                  <Chip
                    @intent="primary"
                    @size="sm"
                    {{pop.register}}
                    {{pop.hoverHandlers}}
                    tabindex="0"
                    class="cursor-help w-fit"
                  >
                    <PhUserCheck @size="12" />
                    {{#if this.isOboByCurrentEmployee}}
                      {{t "pslf.admin_forms.submitted_by_you"}}
                    {{else}}
                      {{t "common.pslf.forms.submitted_by_approver"}}
                    {{/if}}
                  </Chip>
                </:trigger>
                <:body>
                  <div class="p-3 max-w-xs">
                    {{#if this.isOboByCurrentEmployee}}
                      {{t "pslf.admin_forms.submitted_by_you_tooltip"}}
                    {{else}}
                      {{t "pslf.admin_forms.submitted_by_approver_tooltip"}}
                    {{/if}}
                  </div>
                </:body>
              </Popover>
              {{#if date}}
                <span class="text-sm text-gray-500">
                  {{safeFormatDate date month="short" day="2-digit" year="numeric" timeZone="UTC"}}
                </span>
              {{/if}}
            </div>
          {{else if date}}
            {{safeFormatDate date month="short" day="2-digit" year="numeric" timeZone="UTC"}}
          {{else}}
            {{t "pslf.table.pending"}}
          {{/if}}
        </td>
      {{/let}}
      {{#let this.normalizedEmployerSignDate as |date|}}
        <td class={{@cellClass}} title={{date}}>
          {{#if date}}
            {{safeFormatDate date month="short" day="2-digit" year="numeric" timeZone="UTC"}}
          {{else if (or @model.formPrintAndMail @model.employerClosed)}}
            {{t "pslf.table.not_applicable"}}
          {{else if @model.employerSignDate}}
            {{t "pslf.table.pending"}}
          {{/if}}
        </td>
      {{/let}}
      <td class={{@cellClass}}>
        <div class="truncate">
          {{t (concat "common.pslf.form-type." this.formTypeDisplayKey)}}
          {{#if this.isPriorEmployerForm}}
            -
            {{@model.employerName}}
          {{/if}}
        </div>
      </td>
      <td class={{@cellClass}}>
        <Button
          data-test-view-details-button
          @intent="primary"
          @appearance="minimal"
          @icon={{PhEye}}
          title={{t "pslf.table.view_details"}}
          {{on "click" this.openDetailsModal}}
        />
      </td>
    </tr>

    <Modal @isOpen={{this.showDetailsModal}} @onClose={{this.closeDetailsModal}} @size="lg">
      <:header>{{t "pslf.table.form_details"}}</:header>
      <:body>
        {{#if this.isLoadingDetails}}
          <p class="text-gray-500">{{t "common.reporting.loading"}}</p>
        {{else if this.formDetails}}
          <VStack>
            <DefinitionList as |options|>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "pslf.table.form_id"}}</options.dt>
                <options.dd>{{@model.formId}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.form_type"}}</options.dt>
                <options.dd>{{t
                    (concat "common.pslf.form-type." this.formTypeDisplayKey)
                  }}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "pslf.table.status"}}</options.dt>
                <options.dd>
                  {{#if this.needsReview}}
                    <LinkTo
                      @route="authenticated.admin.pslf.form.review"
                      @model={{@model.formId}}
                      class="inline-flex items-center gap-1 text-red-600 font-semibold underline hover:text-red-800"
                      data-test-modal-review-link
                    >
                      {{t "common.pslf.status.needs_review"}}
                      <PhArrowRight @size="14" />
                    </LinkTo>
                  {{else}}
                    {{t (concat "common.pslf.status." @model.formStatus)}}
                  {{/if}}
                </options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.employer_name"}}</options.dt>
                <options.dd>{{or this.formDetails.employerDisplayName "—"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.fein"}}</options.dt>
                <options.dd>{{or this.formFein "—"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.employment_dates"}}</options.dt>
                <options.dd>
                  {{#if this.employmentStartDate}}
                    {{safeFormatDate
                      this.employmentStartDate
                      month="short"
                      day="2-digit"
                      year="numeric"
                      timeZone="UTC"
                    }}
                    —
                    {{#if (eq this.employmentEndDate (t "pslf.table.present"))}}
                      {{t "pslf.table.present"}}
                    {{else if this.employmentEndDate}}
                      {{safeFormatDate
                        this.employmentEndDate
                        month="short"
                        day="2-digit"
                        year="numeric"
                        timeZone="UTC"
                      }}
                    {{else}}
                      —
                    {{/if}}
                  {{else}}
                    —
                  {{/if}}
                </options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.weekly_hours"}}</options.dt>
                <options.dd>{{or this.employmentHours "—"}}</options.dd>
              </div>
              {{#if this.normalizedBorrowerSignDate}}
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "pslf.table.borrower_signed_date"}}</options.dt>
                  <options.dd>
                    {{safeFormatDate
                      this.normalizedBorrowerSignDate
                      month="short"
                      day="2-digit"
                      year="numeric"
                      timeZone="UTC"
                    }}
                  </options.dd>
                </div>
              {{/if}}
              {{#if this.normalizedEmployerSignDate}}
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "pslf.table.employer_signed_date"}}</options.dt>
                  <options.dd>
                    {{safeFormatDate
                      this.normalizedEmployerSignDate
                      month="short"
                      day="2-digit"
                      year="numeric"
                      timeZone="UTC"
                    }}
                  </options.dd>
                </div>
              {{/if}}
            </DefinitionList>

            <Divider class="my-4" />

            <div class="flex items-center gap-4">
              {{#if (or @model.borrowerSignDate @model.employerSignDate)}}
                <DownloadButton
                  @needsUrl={{this.needsUrl}}
                  @filename={{concat @model.borrowerName " - " @model.formId}}
                />
              {{/if}}

              {{#if this.formDetails.isNonPartner}}
                {{#if this.formDetails.employerSignLink}}
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @icon={{PhLink}}
                    title={{t "pslf.table.copy_sign_link"}}
                    class="inline-flex items-center gap-1"
                    @label={{t "pslf.table.copy_sign_link"}}
                    {{on "click" this.copySignLink}}
                  />
                {{/if}}
              {{/if}}
            </div>
          </VStack>
        {{/if}}
      </:body>
    </Modal>
  </template>
}
